/**
 * PR-ONB-WIZARD-AREA-UX-1 — pure view-models / copy for the resumable
 * onboarding wizard + area UX wave. No React, no DOM → unit-testable in
 * isolation (see `__tests__/onboardingUxViews.test.ts`).
 */

// ─── 1. Post-account success banner ──────────────────────────────────────────
// Human copy: no email in the banner, no developer wording ("server").
export const ACCOUNT_CREATED_BANNER = {
  title: 'Accesso creato',
  message:
    'Accesso creato correttamente. Potrai uscire e riprendere la richiesta in qualsiasi momento.',
} as const;

// ─── 4. Area "Documenti" footer (submit lives in the wizard summary) ─────────
export const AREA_DOCS_RETURN_TO_SUMMARY = {
  helper:
    'Quando hai caricato i documenti richiesti, torna al riepilogo per inviare la richiesta.',
  ctaLabel: 'Torna al riepilogo',
  /** Wizard review step (where the actual submit happens). */
  ctaTarget: '/registrazione-intermediario/riepilogo',
} as const;

// ─── 3. DOCUMENTS_INCOMPLETE card on the summary review ──────────────────────
export interface MissingDocumentsCardView {
  title: string;
  /** Human labels of the missing document types (may be empty if unknown). */
  items: string[];
  ctaLabel: string;
  /** Area documents section (where required docs are uploaded). */
  ctaTarget: string;
}

export function buildMissingDocumentsCard(
  missingLabels: readonly string[],
): MissingDocumentsCardView {
  return {
    title: 'Documenti obbligatori mancanti',
    items: [...missingLabels],
    ctaLabel: 'Vai ai documenti',
    ctaTarget: '/area/documenti',
  };
}

// ─── 5. "Salva ed esci" affordance across its states ─────────────────────────
//
// idle     → enabled, plain label.
// flushing → disabled, "Salvataggio…" (a short, real in-flight save).
// slow     → still saving but it's taking too long → keep disabled BUT offer
//            an explicit "Esci comunque" escape so the user is never stuck.
// failed   → flush returned dirty/failed → enabled retry + "Esci comunque".
export type SaveAndExitState = 'idle' | 'flushing' | 'slow' | 'failed';

export interface SaveAndExitButtonView {
  label: string;
  disabled: boolean;
  /** Render the "Esci comunque" escape (slow flush, or after a failed flush). */
  showForceExit: boolean;
}

export function saveAndExitButtonView(
  state: SaveAndExitState,
): SaveAndExitButtonView {
  switch (state) {
    case 'flushing':
      return { label: 'Salvataggio…', disabled: true, showForceExit: false };
    case 'slow':
      return { label: 'Salvataggio…', disabled: true, showForceExit: true };
    case 'failed':
      return { label: 'Salva ed esci', disabled: false, showForceExit: true };
    case 'idle':
    default:
      return { label: 'Salva ed esci', disabled: false, showForceExit: false };
  }
}

/** Flush taking longer than this is treated as "slow" → offer the escape. */
export const SAVE_AND_EXIT_SLOW_MS = 6_000;
